import { CSSProperties } from 'react';
import InfoTip from './InfoTip';
import { GlossaryKey, isGlossaryKey } from './glossary';

interface MoneyProps {
  /** The figure. `null`/`undefined` renders an em dash (value not available yet). */
  amount: number | null | undefined;
  /**
   * BR-31 — mandatory. Every rendered figure must point at a glossary entry; an unknown key
   * throws in dev/test (and fails `Money.test.tsx`), and degrades to "no info icon" in production.
   */
  glossaryKey: GlossaryKey;
  /** `aed` (default) → "AED 1,234.56"; `count` → "12" for integer figures that still need a glossary entry. */
  format?: 'aed' | 'count';
  /** Hide the ⓘ when the enclosing label already carries one for this same key. */
  hideTip?: boolean;
  style?: CSSProperties;
  className?: string;
}

const AED = new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COUNT = new Intl.NumberFormat('en-AE', { maximumFractionDigits: 0 });

export function formatAed(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  return `${sign}AED ${AED.format(Math.abs(amount))}`;
}

const isDevOrTest = import.meta.env.DEV || import.meta.env.MODE === 'test';

/**
 * Spec 053 BR-31 — the only way a financial figure reaches an admin screen.
 * Tabular numerals so columns of money line up; the ⓘ carries the glossary explanation.
 */
export default function Money({ amount, glossaryKey, format = 'aed', hideTip = false, style, className }: MoneyProps) {
  if (!isGlossaryKey(glossaryKey)) {
    const msg = `<Money> rendered with glossaryKey "${String(glossaryKey)}" that is missing from FINANCE_GLOSSARY (BR-31)`;
    if (isDevOrTest) throw new Error(msg);
    console.error(msg);
  }

  const text = amount === null || amount === undefined
    ? '—'
    : format === 'count' ? COUNT.format(amount) : formatAed(amount);

  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', ...style }}>
      <span data-money={glossaryKey} style={{ fontVariantNumeric: 'tabular-nums' }}>{text}</span>
      {!hideTip && isGlossaryKey(glossaryKey) && <InfoTip glossaryKey={glossaryKey} />}
    </span>
  );
}
